import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { ChartAreaInteractive } from "../components/Chart";
import { SimpleTable } from "@/components/SimpleTable";
import { useOutletContext } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  competitorsAPI,
  dataChatAPI,
  onboardingGuideAPI,
  visabilityAPI,
  type ChartData,
  type CompetitorDashboardWidgetState,
  type DashboardCard,
} from "@/services/api.tsx";
import type { Brand } from "@/components/AppSidebar.tsx";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { canonicalizeDomain } from "@/utils/domain";
import { useVisibilityRealtime } from "@/hooks/useVisibilityRealtime";
import { useCompetitorsRealtime } from "@/hooks/useCompetitorsRealtime";
import type { DateRangeValue } from "@/components/DateRangeFilter";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { remoteConfig } from "@/firebase";
import {
  capturePosthogEvent,
  getTimeSinceBrandOnboardingSeconds,
  isFirstDashboardView,
} from "@/lib/posthog";
import { useAuth } from "@/components/AuthContext";
import { Button } from "@/components/ui/button";
import { Presentation, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const defaultData: ChartData = {
  chartData: [],
  citedDomainsVisibility: {},
  citedBrandsVisibility: {},
  competitorsVisibility: {},
  competitorsPosition: {},
  citedPagesStats: {},
  mentionedPagesStats: {},
  data: [],
  mainBrandVisibility: null,
  hasPendingExecutions: false,
};
const defaultCompetitors: [] = [];
const DEFAULT_GUIDANCE_BANNER_COOLDOWN_HOURS = 168;
const DEFAULT_GUIDANCE_BANNER_ENABLED = true;
const DASHBOARD_SECONDARY_QUERY_DELAY_MS = 1200;

const isPendingExecutionStatus = (status?: string | null): boolean => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  return (
    normalized === "running" ||
    normalized === "queued" ||
    normalized === "snapshot_pending" ||
    normalized === "processing" ||
    normalized === "in_progress" ||
    normalized === "in progress" ||
    normalized === "never_run" ||
    normalized === "not_triggered"
  );
};

const hasPendingExecutions = (runs: unknown): boolean => {
  if (!Array.isArray(runs)) return false;
  return runs.some((run: any) => {
    const responseText = String(run?.responseText || "")
      .trim()
      .toLowerCase();
    if (responseText === "in progress") return true;
    const statuses = run?.promptProviderStatuses;
    if (statuses && typeof statuses === "object") {
      return Object.values(statuses as Record<string, unknown>).some((status) =>
        isPendingExecutionStatus(String(status || "")),
      );
    }
    return false;
  });
};

const hasDashboardVisibilityData = (payload: ChartData): boolean => {
  const chartData = Array.isArray(payload?.chartData) ? payload.chartData : [];
  const citedDomains = payload?.citedDomainsVisibility || {};
  const citedBrands = payload?.citedBrandsVisibility || {};
  const competitors = payload?.competitorsVisibility || {};
  return (
    chartData.length > 0 ||
    Object.keys(citedDomains).length > 0 ||
    Object.keys(citedBrands).length > 0 ||
    Object.keys(competitors).length > 0
  );
};

const parseMetricNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const direct = Number(trimmed.replace("%", "").trim());
  if (Number.isFinite(direct)) return direct;

  const match = trimmed.match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeAliasKey = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9.]+/g, " ")
    .trim();

const compactAliasKey = (value: string): string =>
  normalizeAliasKey(value).replace(/\s+/g, "");

const parseGuidanceBannerEnabled = (value: string): boolean => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return DEFAULT_GUIDANCE_BANNER_ENABLED;
  if (["true", "1", "yes", "on"].includes(normalized)) return true;
  if (["false", "0", "no", "off"].includes(normalized)) return false;
  return DEFAULT_GUIDANCE_BANNER_ENABLED;
};

const getGuidanceBannerStorageKey = (
  userId?: string | null,
  email?: string | null,
): string => {
  const identity = String(userId || email || "anonymous")
    .trim()
    .toLowerCase();
  return `guidance_banner_dismissed_at:${identity || "anonymous"}`;
};

const getCompetitorsBannerDismissedStorageKey = (
  userId?: string | null,
  email?: string | null,
  brandId?: string | null,
): string => {
  const identity = String(userId || email || "anonymous")
    .trim()
    .toLowerCase();
  const normalizedBrand = String(brandId || "unknown")
    .trim()
    .toLowerCase();
  return `competitors_banner_dismissed:${identity || "anonymous"}:${normalizedBrand || "unknown"}`;
};

export default function DashboardPage() {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentBrand, selectedProviders, setDashboardPrimaryReady } =
    useOutletContext<{
      currentBrand: Brand;
      selectedCountries: string[];
      selectedProviders?: string[];
      setDashboardPrimaryReady?: (ready: boolean) => void;
    }>();
  const navigate = useNavigate();
  const viewedGuidanceBannerRef = useRef(false);
  const competitorsLoadingShownRef = useRef<string>("");
  const competitorsBannerViewedRef = useRef<string>("");
  const [competitorsBannerDismissed, setCompetitorsBannerDismissed] =
    useState(false);
  const [guidanceBannerDismissedAtMs, setGuidanceBannerDismissedAtMs] =
    useState<number | null>(null);
  const guidanceBannerStorageKey = useMemo(
    () => getGuidanceBannerStorageKey(user?.id, user?.email),
    [user?.email, user?.id],
  );
  const competitorsBannerStorageKey = useMemo(
    () =>
      getCompetitorsBannerDismissedStorageKey(
        user?.id,
        user?.email,
        currentBrand?.brandId || currentBrand?.id,
      ),
    [currentBrand?.brandId, currentBrand?.id, user?.email, user?.id],
  );

  const { timeRange, setTimeRange, dateRange } = useOutletContext<{
    timeRange: string;
    setTimeRange: (time: string) => void;
    dateRange?: DateRangeValue;
  }>();

  const normalizedDays = dateRange?.days || parseInt(timeRange);
  const dateRangeKey = dateRange
    ? `${dateRange.startDate}:${dateRange.endDate}`
    : timeRange;
  const requestDateRange =
    dateRange?.preset === "custom" || dateRange?.preset === "yesterday"
      ? dateRange
      : undefined;
  const providersKey = selectedProviders?.join(",") || "all";
  const dashboardCompetitorsQueryKey = [
    "dashboardCompetitors",
    currentBrand?.brandId,
    providersKey,
  ];
  const dashboardCardsQueryKey = [
    "dashboard-cards",
    currentBrand?.brandId || currentBrand?.id,
  ];
  const trackedDashboardRef = useRef<string>("");
  const visibilityQueryKey = [
    "visability",
    currentBrand?.brandId,
    timeRange,
    dateRangeKey,
    providersKey,
  ];
  const isDashboardVisibilityRealtimeEnabled = Boolean(
    currentBrand?.brandId &&
      timeRange &&
      dateRange?.preset !== "custom" &&
      dateRange?.preset !== "yesterday",
  );

  const [secondaryQueriesReady, setSecondaryQueriesReady] = useState(false);

  useEffect(() => {
    setSecondaryQueriesReady(false);
    if (!currentBrand?.brandId || !timeRange) return;
    const timeout = window.setTimeout(
      () => setSecondaryQueriesReady(true),
      DASHBOARD_SECONDARY_QUERY_DELAY_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [currentBrand?.brandId, timeRange, dateRangeKey, providersKey]);

  const {
    data = defaultData,
    isLoading: isRunResultLoading,
    isFetched: isRunResultFetched,
  } = useQuery({
    queryKey: visibilityQueryKey,
    queryFn: async () => {
      const res = await visabilityAPI.getRunResults(
        currentBrand,
        normalizedDays,
        selectedProviders,
        undefined,
        false,
        false,
        requestDateRange,
        false,
        false,
      );
      return res.data;
    },
    staleTime: isDashboardVisibilityRealtimeEnabled ? Infinity : 60_000,
    gcTime: isDashboardVisibilityRealtimeEnabled ? Infinity : 5 * 60 * 1000,
    refetchOnMount: isDashboardVisibilityRealtimeEnabled ? false : "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: isDashboardVisibilityRealtimeEnabled
      ? false
      : 4 * 60 * 1000,
    refetchIntervalInBackground: false,
    enabled: !!currentBrand?.brandId && !!timeRange,
  });

  useEffect(() => {
    if (!currentBrand?.brandId || isRunResultLoading) return;
    setSecondaryQueriesReady(true);
  }, [currentBrand?.brandId, isRunResultLoading]);

  useEffect(() => {
    if (!currentBrand?.brandId || !isRunResultFetched) return;
    setDashboardPrimaryReady?.(true);
  }, [currentBrand?.brandId, isRunResultFetched, setDashboardPrimaryReady]);

  const secondaryQueriesEnabled =
    Boolean(currentBrand?.brandId) && secondaryQueriesReady;

  const { data: guidanceBannerConfig } = useQuery({
    queryKey: ["remoteConfig", "guidanceBanner"],
    queryFn: async () => {
      await fetchAndActivate(remoteConfig);
      const url = getValue(remoteConfig, "guidance_banner_url")
        .asString()
        .trim();
      const enabled = parseGuidanceBannerEnabled(
        getValue(remoteConfig, "guidance_banner_enabled").asString(),
      );
      const configuredCooldown = getValue(
        remoteConfig,
        "guidance_banner_dismiss_cooldown_hours",
      ).asNumber();
      const cooldownHours =
        Number.isFinite(configuredCooldown) && configuredCooldown > 0
          ? configuredCooldown
          : DEFAULT_GUIDANCE_BANNER_COOLDOWN_HOURS;
      return {
        enabled,
        url,
        cooldownHours,
      };
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: secondaryQueriesEnabled,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const raw = window.localStorage.getItem(guidanceBannerStorageKey);
    if (!raw) {
      setGuidanceBannerDismissedAtMs(null);
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setGuidanceBannerDismissedAtMs(null);
      return;
    }
    setGuidanceBannerDismissedAtMs(parsed);
  }, [guidanceBannerStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const raw = window.localStorage.getItem(competitorsBannerStorageKey);
    setCompetitorsBannerDismissed(raw === "true");
  }, [competitorsBannerStorageKey]);

  const guidanceBannerCooldownMs = useMemo(() => {
    const hours =
      guidanceBannerConfig?.cooldownHours ||
      DEFAULT_GUIDANCE_BANNER_COOLDOWN_HOURS;
    return hours * 60 * 60 * 1000;
  }, [guidanceBannerConfig?.cooldownHours]);

  const isGuidanceBannerCooldownActive = useMemo(() => {
    if (!guidanceBannerDismissedAtMs) return false;
    return Date.now() - guidanceBannerDismissedAtMs < guidanceBannerCooldownMs;
  }, [guidanceBannerDismissedAtMs, guidanceBannerCooldownMs]);

  const shouldShowGuidanceBanner =
    Boolean(guidanceBannerConfig?.enabled) &&
    Boolean(guidanceBannerConfig?.url) &&
    !isGuidanceBannerCooldownActive;

  const { data: dashboardCards = [] } = useQuery({
    queryKey: dashboardCardsQueryKey,
    queryFn: async () => {
      const brandId = currentBrand?.brandId || currentBrand?.id;
      if (!brandId) return [];
      const response = await dataChatAPI.getDashboardCards(brandId);
      return response.cards;
    },
    enabled: secondaryQueriesEnabled,
    staleTime: 60_000,
  });

  const deleteDashboardCard = useMutation({
    mutationFn: async (cardId: string) => {
      const brandId = currentBrand?.brandId || currentBrand?.id;
      if (!brandId) throw new Error("Missing brand");
      return dataChatAPI.deleteDashboardCard(brandId, cardId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: dashboardCardsQueryKey });
      toast.success(t("legacy.dashboard.card.removed.19hmkhj"));
    },
    onError: () => {
      toast.error(t("legacy.couldnt.remove.dashboard.card.v92mzj"));
    },
  });

  useVisibilityRealtime({
    brandId: currentBrand?.brandId,
    days: normalizedDays,
    providers: selectedProviders,
    queryKey: visibilityQueryKey,
    enabled: isDashboardVisibilityRealtimeEnabled,
  });

  const canonicalBrandDomain = canonicalizeDomain(currentBrand?.url);
  const hasRunResults =
    Number((data as ChartData)?.promptRunCount || 0) > 0 ||
    (Array.isArray(data?.data) && data.data.length > 0);
  const hasPendingRunExecutions =
    Boolean((data as ChartData)?.hasPendingExecutions) ||
    hasPendingExecutions(data?.data);
  const shouldShowMainLoaders =
    isRunResultLoading ||
    (!hasDashboardVisibilityData(data) && hasPendingRunExecutions);

  const { data: competitorsResponse, isLoading: isCompetitorsLoading } =
    useQuery({
      queryKey: dashboardCompetitorsQueryKey,
      queryFn: async () => {
        const res = await competitorsAPI.getCompetitors(
          currentBrand.brandId,
          selectedProviders,
        );
        return {
          competitors: res.competitors || [],
          competitorWidget: res.competitorWidget,
        };
      },
      enabled: secondaryQueriesEnabled,
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: (query) => {
        const payload = query.state.data as
          | {
              competitors?: any[];
              competitorWidget?: CompetitorDashboardWidgetState;
            }
          | undefined;
        if (payload?.competitorWidget?.phase === "loading") {
          return 30_000;
        }
        return false;
      },
      refetchIntervalInBackground: false,
    });

  const competitorsData =
    competitorsResponse?.competitors || defaultCompetitors;
  const competitorWidget: CompetitorDashboardWidgetState | null =
    competitorsResponse?.competitorWidget || null;

  useCompetitorsRealtime({
    brandId: currentBrand?.brandId,
    providers: selectedProviders,
    queryKey: dashboardCompetitorsQueryKey,
    enabled: secondaryQueriesEnabled,
  });
  const { data: onboardingGuideState = null } = useQuery({
    queryKey: ["onboardingGuide", currentBrand?.brandId],
    queryFn: async () => {
      if (!currentBrand?.brandId) return null;
      const res = await onboardingGuideAPI.getState(currentBrand.brandId);
      return res?.state || null;
    },
    enabled: secondaryQueriesEnabled,
    staleTime: Infinity,
  });

  const brandDomainAliases = useMemo(() => {
    const map = new Map<string, string>();
    const addAlias = (key?: string | null, domain?: string | null) => {
      if (!key || !domain) return;
      const normalizedDomain =
        canonicalizeDomain(domain) || domain.toLowerCase();
      const normalizedKey = String(key).toLowerCase();
      map.set(normalizedKey, normalizedDomain);
      const canonicalKey = canonicalizeDomain(normalizedKey);
      if (canonicalKey) {
        map.set(canonicalKey, normalizedDomain);
      }
      const stem = normalizedDomain.split(".")[0];
      if (stem) {
        map.set(stem.toLowerCase(), normalizedDomain);
      }
      const normalizedAlias = normalizeAliasKey(normalizedKey);
      if (normalizedAlias) {
        map.set(normalizedAlias, normalizedDomain);
        const firstToken = normalizedAlias.split(" ")[0];
        if (firstToken) {
          map.set(firstToken, normalizedDomain);
        }
        const compactAlias = normalizedAlias.replace(/\s+/g, "");
        if (compactAlias) {
          map.set(compactAlias, normalizedDomain);
        }
      }
    };
    if (canonicalBrandDomain) {
      map.set(canonicalBrandDomain.toLowerCase(), canonicalBrandDomain);
      addAlias(currentBrand?.name, canonicalBrandDomain);
      addAlias(currentBrand?.url, canonicalBrandDomain);
    }
    competitorsData.forEach((competitor: any) => {
      const domain = canonicalizeDomain(competitor.domain);
      if (!domain) return;
      const brandKey = competitor.brandKey;
      const brandDisplay = competitor.brandDisplay;
      const name = competitor.name;
      map.set(domain.toLowerCase(), domain);
      addAlias(competitor.domain, domain);
      addAlias(brandKey, domain);
      addAlias(brandDisplay, domain);
      addAlias(name, domain);
    });
    return map;
  }, [
    canonicalBrandDomain,
    competitorsData,
    currentBrand?.name,
    currentBrand?.url,
  ]);

  const handleShowAll = () => {
    const brandSearch =
      currentBrand?.brandId || currentBrand?.id
        ? `?brandId=${currentBrand.brandId || currentBrand.id}`
        : "";
    navigate(`/dashboard/citations/cited-domains${brandSearch}`);
  };

  const handleShowAllCompetitors = () => {
    const brandSearch =
      currentBrand?.brandId || currentBrand?.id
        ? `?brandId=${currentBrand.brandId || currentBrand.id}`
        : "";
    navigate(`/dashboard/competitors${brandSearch}`);
  };

  const isCompetitorsWidgetLoading =
    competitorWidget?.phase === "loading" || false;
  const shouldShowCompetitorsLoadingState =
    isCompetitorsWidgetLoading && hasPendingRunExecutions;
  const isActivateCompetitorsStepDone = Boolean(
    onboardingGuideState?.steps?.activateCompetitors,
  );
  const isCompetitorsTableLoading =
    isCompetitorsLoading ||
    shouldShowCompetitorsLoadingState ||
    isRunResultLoading;
  const shouldShowCompetitorsBanner = Boolean(
    competitorWidget?.phase === "results" &&
      competitorWidget?.autoAdded &&
      !isActivateCompetitorsStepDone &&
      !competitorsBannerDismissed,
  );

  const DASHBOARD_COMPETITORS_LIMIT = 10;

  const competitorsMemo = useMemo(() => {
    const competitorsVisibilityMap = new Map<string, number>();
    Object.entries(data.competitorsVisibility || {}).forEach(([key, value]) => {
      const rawKey = String(key || "");
      const lowered = rawKey.toLowerCase();
      const canonical = canonicalizeDomain(lowered) || lowered;
      const normalized = normalizeAliasKey(lowered);
      const compact = compactAliasKey(lowered);
      const resolved =
        brandDomainAliases.get(lowered) ||
        brandDomainAliases.get(canonical) ||
        brandDomainAliases.get(compact) ||
        (normalized
          ? brandDomainAliases.get(normalized) ||
            brandDomainAliases.get(normalized.replace(/\s+/g, "")) ||
            brandDomainAliases.get(normalized.split(" ")[0])
          : null) ||
        canonical;
      const numeric = parseMetricNumber(value);
      if (numeric !== null && resolved) {
        competitorsVisibilityMap.set(resolved, numeric);
      }
    });

    const competitorsPositionMap = new Map<string, number>();
    const competitorsPositionServerMap = new Map<string, number>();
    Object.entries(data.competitorsPosition || {}).forEach(([key, value]) => {
      const rawKey = String(key || "");
      const lowered = rawKey.toLowerCase();
      const canonical = canonicalizeDomain(lowered) || lowered;
      const normalized = normalizeAliasKey(lowered);
      const compact = compactAliasKey(lowered);
      const resolved =
        brandDomainAliases.get(lowered) ||
        brandDomainAliases.get(canonical) ||
        brandDomainAliases.get(compact) ||
        (normalized
          ? brandDomainAliases.get(normalized) ||
            brandDomainAliases.get(normalized.replace(/\s+/g, "")) ||
            brandDomainAliases.get(normalized.split(" ")[0])
          : null) ||
        canonical;
      const numeric = parseMetricNumber(value);
      if (numeric !== null && resolved) {
        competitorsPositionServerMap.set(resolved, numeric);
      }
    });

    const runs = Array.isArray(data.data) ? data.data : [];
    const runsForStats = runs.filter((run: any) => {
      const brands = Array.isArray(run?.citedBrands) ? run.citedBrands : [];
      return brands.length > 0;
    });
    const selectedDomains = new Set<string>();
    if (canonicalBrandDomain) {
      selectedDomains.add(canonicalBrandDomain);
    }
    competitorsData.forEach((competitor: any) => {
      const canonical = canonicalizeDomain(competitor.domain);
      if (canonical) selectedDomains.add(canonical);
    });

    const aggregate = new Map<string, { sum: number; count: number }>();
    const runMentionCountMap = new Map<string, number>();
    const totalRunsForVisibility = runs.length;
    runsForStats.forEach((run: any) => {
      const brands = Array.isArray(run?.citedBrands) ? run.citedBrands : [];
      const runPositions = new Map<string, number>();
      brands.forEach((brand: any) => {
        const position = Number(brand?.position);
        if (!Number.isFinite(position) || position <= 0) return;
        let domain: string | null = null;
        if (brand?.isMain && canonicalBrandDomain) {
          domain = canonicalBrandDomain;
        } else {
          const rawDomain =
            typeof brand?.domain === "string"
              ? brand.domain
              : typeof brand?.url === "string"
                ? brand.url
                : null;
          if (rawDomain) {
            const rawKey = String(rawDomain).toLowerCase();
            const canonicalRaw = canonicalizeDomain(rawKey) || rawKey;
            const normalizedRaw = normalizeAliasKey(rawKey);
            const compactRaw = compactAliasKey(rawKey);
            domain =
              brandDomainAliases.get(rawKey) ||
              brandDomainAliases.get(canonicalRaw) ||
              brandDomainAliases.get(compactRaw) ||
              (normalizedRaw
                ? brandDomainAliases.get(normalizedRaw) ||
                  brandDomainAliases.get(normalizedRaw.replace(/\s+/g, "")) ||
                  brandDomainAliases.get(normalizedRaw.split(" ")[0])
                : null) ||
              canonicalizeDomain(rawDomain) ||
              rawKey;
          } else {
            const key = String(
              brand?.brandKey || brand?.brandDisplay || "",
            ).toLowerCase();
            if (key) {
              const normalizedKey = normalizeAliasKey(key);
              domain =
                brandDomainAliases.get(key) ||
                brandDomainAliases.get(compactAliasKey(key)) ||
                (normalizedKey
                  ? brandDomainAliases.get(normalizedKey) ||
                    brandDomainAliases.get(normalizedKey.replace(/\s+/g, "")) ||
                    brandDomainAliases.get(normalizedKey.split(" ")[0])
                  : null) ||
                null;
              if (!domain) {
                const canonicalKey = canonicalizeDomain(key) || key;
                domain = canonicalKey;
              }
            }
          }
        }
        if (!domain || !selectedDomains.has(domain)) return;
        const existing = runPositions.get(domain);
        if (!existing || position < existing) {
          runPositions.set(domain, position);
        }
      });

      if (!runPositions.size) return;

      runPositions.forEach((_, domain) => {
        const count = runMentionCountMap.get(domain) || 0;
        runMentionCountMap.set(domain, count + 1);
      });

      const sorted = Array.from(runPositions.entries()).sort(
        (a, b) => a[1] - b[1],
      );
      sorted.forEach(([domain], index) => {
        const entry = aggregate.get(domain) || { sum: 0, count: 0 };
        entry.sum += index + 1;
        entry.count += 1;
        aggregate.set(domain, entry);
      });
    });

    aggregate.forEach((value, domain) => {
      if (!value.count) return;
      competitorsPositionMap.set(domain, value.sum / value.count);
    });
    const competitorsVisibilityFallbackMap = new Map<string, number>();
    runMentionCountMap.forEach((count, domain) => {
      if (!totalRunsForVisibility) return;
      competitorsVisibilityFallbackMap.set(
        domain,
        (count / totalRunsForVisibility) * 100,
      );
    });

    const uniqueByDomain = new Map<string, { domain: string }>();

    competitorsData.forEach((competitor: any) => {
      const canonical = canonicalizeDomain(competitor.domain);
      if (!canonical) return;
      if (!uniqueByDomain.has(canonical)) {
        uniqueByDomain.set(canonical, { ...competitor, domain: canonical });
      }
    });

    if (canonicalBrandDomain && !uniqueByDomain.has(canonicalBrandDomain)) {
      uniqueByDomain.set(canonicalBrandDomain, {
        domain: canonicalBrandDomain,
      });
    }

    let filteredcompetitorsData = Array.from(uniqueByDomain.values())
      .map(
        (item: {
          domain: string;
          visibility?: number;
          position?: number;
          isOwnBrand?: boolean;
        }) => {
          const directVisibility =
            competitorsVisibilityMap.get(item.domain) ??
            competitorsVisibilityFallbackMap.get(item.domain);
          const fallbackVisibility = directVisibility;
          const visibilityValue =
            typeof fallbackVisibility === "number" &&
            !Number.isNaN(fallbackVisibility)
              ? fallbackVisibility
              : 0;
          const rounded = Math.round(visibilityValue);
          const fallbackPosition =
            competitorsPositionServerMap.get(item.domain) ??
            competitorsPositionMap.get(item.domain);
          const position = Number.isFinite(fallbackPosition)
            ? fallbackPosition
            : null;

          return {
            domain: item.domain,
            points: `${rounded}%`,
            visability: rounded,
            position,
            isOwnBrand: Boolean(
              item.isOwnBrand ||
                (canonicalBrandDomain && item.domain === canonicalBrandDomain),
            ),
          };
        },
      )
      .sort(
        (a: { visability: number }, b: { visability: number }) =>
          b.visability - a.visability,
      );

    const mainBrandIndex = filteredcompetitorsData.findIndex(
      (item: { domain: string; isOwnBrand?: boolean }) =>
        Boolean(item.isOwnBrand) ||
        Boolean(canonicalBrandDomain && item.domain === canonicalBrandDomain),
    );
    if (filteredcompetitorsData.length > DASHBOARD_COMPETITORS_LIMIT) {
      if (mainBrandIndex >= DASHBOARD_COMPETITORS_LIMIT) {
        const mainBrandItem = filteredcompetitorsData[mainBrandIndex];
        const topWithMain = filteredcompetitorsData
          .filter((_, index) => index !== mainBrandIndex)
          .slice(0, DASHBOARD_COMPETITORS_LIMIT - 1);
        filteredcompetitorsData = [...topWithMain, mainBrandItem].sort(
          (a: { visability: number }, b: { visability: number }) =>
            b.visability - a.visability,
        );
      } else {
        filteredcompetitorsData = filteredcompetitorsData.slice(
          0,
          DASHBOARD_COMPETITORS_LIMIT,
        );
      }
    }

    const hasOnlyBrand =
      filteredcompetitorsData.length === 1 &&
      Boolean(filteredcompetitorsData[0].isOwnBrand);

    return {
      data: filteredcompetitorsData,
      onlyBrand: hasOnlyBrand,
    };
  }, [
    competitorsData,
    data.competitorsVisibility,
    data.competitorsPosition,
    data.data,
    brandDomainAliases,
    canonicalBrandDomain,
  ]);

  const competitorsTableOptimized = competitorsMemo.data;
  const showOnlyYou = competitorsMemo.onlyBrand;
  const chartBarValuesBySeries = useMemo(() => {
    const values: Record<string, number> = {};
    competitorsTableOptimized.forEach(
      (item: { domain: string; visability?: number }) => {
        const numeric = Number(item.visability);
        if (!Number.isFinite(numeric)) return;
        values[item.domain] = Math.min(100, Math.max(0, Math.round(numeric)));
      },
    );
    return values;
  }, [competitorsTableOptimized]);

  useEffect(() => {
    if (!currentBrand?.brandId || isRunResultLoading) return;
    const key = `${currentBrand.brandId}:${providersKey}:${dateRangeKey}:${hasRunResults}`;
    if (trackedDashboardRef.current === key) return;
    trackedDashboardRef.current = key;

    capturePosthogEvent("dashboard_viewed", {
      brand_id: currentBrand.brandId,
      dashboard: "overview",
      is_first_view: isFirstDashboardView({
        userId: user?.id,
        brandId: currentBrand.brandId,
        dashboard: "overview",
      }),
      has_data: hasRunResults,
      time_since_onboarding_seconds:
        getTimeSinceBrandOnboardingSeconds(currentBrand.brandId) ?? undefined,
    });
  }, [
    currentBrand?.brandId,
    data?.data,
    hasRunResults,
    isRunResultLoading,
    dateRangeKey,
    requestDateRange,
    normalizedDays,
    providersKey,
    selectedProviders,
    user?.id,
  ]);

  useEffect(() => {
    if (!currentBrand?.brandId || !competitorWidget) return;
    if (competitorWidget.phase !== "loading") return;
    const key = currentBrand.brandId;
    if (competitorsLoadingShownRef.current === key) return;
    competitorsLoadingShownRef.current = key;
    capturePosthogEvent("competitors_loading_shown", {
      brand_id: currentBrand.brandId,
      org_id: competitorWidget.orgId || undefined,
      total_prompts: competitorWidget.totalPrompts,
    });
  }, [competitorWidget, currentBrand?.brandId, currentBrand?.id]);

  useEffect(() => {
    if (!shouldShowCompetitorsBanner || !currentBrand?.brandId) return;
    const key = `${currentBrand.brandId}:${competitorsBannerStorageKey}`;
    if (competitorsBannerViewedRef.current === key) return;
    competitorsBannerViewedRef.current = key;
    capturePosthogEvent("competitors_banner_viewed", {
      brand_id: currentBrand.brandId,
      org_id: competitorWidget?.orgId || undefined,
      page: "dashboard",
    });
  }, [
    competitorWidget?.orgId,
    competitorsBannerStorageKey,
    currentBrand?.brandId,
    shouldShowCompetitorsBanner,
  ]);

  useEffect(() => {
    if (!shouldShowGuidanceBanner || viewedGuidanceBannerRef.current) return;
    viewedGuidanceBannerRef.current = true;
    capturePosthogEvent("guidance_banner_viewed", {
      page: "dashboard",
    });
  }, [shouldShowGuidanceBanner]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !isGuidanceBannerCooldownActive ||
      !guidanceBannerDismissedAtMs
    ) {
      return;
    }
    const elapsedMs = Date.now() - guidanceBannerDismissedAtMs;
    const remainingMs = guidanceBannerCooldownMs - elapsedMs;
    if (remainingMs <= 0) {
      setGuidanceBannerDismissedAtMs(null);
      window.localStorage.removeItem(guidanceBannerStorageKey);
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setGuidanceBannerDismissedAtMs(null);
      window.localStorage.removeItem(guidanceBannerStorageKey);
    }, remainingMs);
    return () => window.clearTimeout(timeoutId);
  }, [
    guidanceBannerCooldownMs,
    guidanceBannerDismissedAtMs,
    guidanceBannerStorageKey,
    isGuidanceBannerCooldownActive,
  ]);

  const handleGuidanceBannerClick = () => {
    if (!guidanceBannerConfig?.url) return;
    capturePosthogEvent("guidance_banner_clicked", {
      page: "dashboard",
      url: guidanceBannerConfig.url,
    });
    window.open(guidanceBannerConfig.url, "_blank", "noopener,noreferrer");
  };

  const handleGuidanceBannerDismiss = () => {
    if (typeof window === "undefined") return;
    const dismissedAt = Date.now();
    window.localStorage.setItem(guidanceBannerStorageKey, String(dismissedAt));
    setGuidanceBannerDismissedAtMs(dismissedAt);
    capturePosthogEvent("guidance_banner_dismissed", {
      page: "dashboard",
    });
  };

  const dismissCompetitorsBanner = (trackDismissEvent: boolean) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(competitorsBannerStorageKey, "true");
    }
    setCompetitorsBannerDismissed(true);
    if (trackDismissEvent) {
      capturePosthogEvent("competitors_banner_dismissed", {
        brand_id: currentBrand?.brandId,
        org_id: competitorWidget?.orgId || undefined,
        page: "dashboard",
      });
    }
  };

  const handleAddCompetitorCtaClick = (source: "banner" | "card_button") => {
    if (source === "banner") {
      capturePosthogEvent("competitors_banner_clicked", {
        brand_id: currentBrand?.brandId,
        org_id: competitorWidget?.orgId || undefined,
        page: "dashboard",
      });
      dismissCompetitorsBanner(true);
    } else if (shouldShowCompetitorsBanner && !competitorsBannerDismissed) {
      dismissCompetitorsBanner(true);
    }
    const searchParams = new URLSearchParams();
    const brandId = currentBrand?.brandId || currentBrand?.id;
    if (brandId) {
      searchParams.set("brandId", String(brandId));
    }
    searchParams.set("tab", "suggested");
    navigate(`/dashboard/competitors?${searchParams.toString()}`);
  };

  const renderDashboardCardContent = (card: DashboardCard) => {
    if (card.metricKey === "main_visibility") {
      const value = Math.round(Number(data.mainBrandVisibility || 0));
      return (
        <div>
          <div className="text-3xl font-semibold text-[#0A0A0A]">{value}%</div>
          <p className="mt-2 text-sm text-[#737373]">
            <LocalizedText id="legacy.brand.visibility.across.completed.prompt.results.xnt5ro" />
          </p>
        </div>
      );
    }
    if (card.metricKey === "prompt_count") {
      const count =
        Number((data as ChartData)?.promptRunCount || 0) ||
        (Array.isArray(data.data) ? data.data.length : 0);
      return (
        <div>
          <div className="text-3xl font-semibold text-[#0A0A0A]">{count}</div>
          <p className="mt-2 text-sm text-[#737373]">
            <LocalizedText id="legacy.prompt.results.in.the.selected.window.nbq4ps" />
          </p>
        </div>
      );
    }
    if (card.metricKey === "top_cited_domains") {
      const rows = Object.entries(data.citedDomainsVisibility || {})
        .map(([domain, value]) => ({
          label: domain,
          value: Math.round(Number(value || 0)),
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, Number(card.config?.limit || 5));
      return rows.length ? (
        <div className="space-y-2">
          {rows.map((row) => (
            <div
              key={row.label}
              className="flex items-center justify-between gap-3 text-sm"
            >
              <span className="min-w-0 truncate text-[#171717]">
                {row.label}
              </span>
              <span className="font-medium text-[#0A0A0A]">{row.value}%</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-[#737373]">
          <LocalizedText id="legacy.no.cited.domain.data.yet.mh1e4g" />
        </p>
      );
    }
    if (card.metricKey === "top_competitors") {
      const rows = competitorsTableOptimized
        .filter((item: { isOwnBrand?: boolean }) => !item.isOwnBrand)
        .slice(0, Number(card.config?.limit || 5));
      return rows.length ? (
        <div className="space-y-3">
          {rows.map((row: { domain: string; visability?: number }) => {
            const value = Math.max(
              0,
              Math.min(100, Number(row.visability || 0)),
            );
            return (
              <div key={row.domain}>
                <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate text-[#171717]">
                    {row.domain}
                  </span>
                  <span className="font-medium text-[#0A0A0A]">
                    {Math.round(value)}%
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-[#F0F0F0]">
                  <div
                    className="h-full rounded-full bg-[#FF5E00]"
                    style={{ width: `${value}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-[#737373]">
          <LocalizedText id="legacy.no.competitor.data.yet.1l7qbv6" />
        </p>
      );
    }
    if (card.metricKey === "visibility_trend") {
      const latest = Array.isArray(data.chartData)
        ? data.chartData[data.chartData.length - 1]
        : null;
      const value =
        latest && canonicalBrandDomain
          ? Math.round(Number(latest[canonicalBrandDomain] || 0))
          : Math.round(Number(data.mainBrandVisibility || 0));
      return (
        <div>
          <div className="text-3xl font-semibold text-[#0A0A0A]">{value}%</div>
          <p className="mt-2 text-sm text-[#737373]">
            <LocalizedText id="legacy.latest.point.in.the.selected.visibility.trend.1t4g7ii" />
          </p>
        </div>
      );
    }
    return (
      <p className="text-sm text-[#737373]">
        <LocalizedText id="legacy.this.card.is.waiting.for.a.connected.data.eilvds" />
      </p>
    );
  };

  return (
    <div>
      {shouldShowGuidanceBanner && (
        <div className="mb-6 w-full rounded-[12px] bg-[#F5F5F5] p-4">
          <div className="flex w-full flex-wrap items-center gap-2 sm:flex-nowrap">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-[#FF5E00]">
              <Presentation className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold leading-5 text-[#0A0A0A]">
                <LocalizedText id="legacy.want.guidance.book.a.free.1.1.call.kjim3o" />
              </p>
              <p className="text-xs leading-4 text-[#0A0A0A]">
                <LocalizedText id="legacy.well.demo.the.platform.and.show.how.to.dwmz3x" />
              </p>
            </div>
            <Button
              type="button"
              onClick={handleGuidanceBannerClick}
              className="h-8 cursor-pointer rounded-[8px] bg-[#FF5E00] px-3 text-sm font-medium text-[#FAFAFA] shadow-[0px_1px_2px_rgba(0,0,0,0.1)] hover:bg-[#ff8138] hover:text-[#FAFAFA]"
            >
              <LocalizedText id="legacy.book.a.call.1lrete7" />
            </Button>
            <button
              type="button"
              aria-label={t("legacy.dismiss.guidance.banner.eu2jul")}
              onClick={handleGuidanceBannerDismiss}
              className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-[6px] border border-[#E5E5E5] bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.1)]"
            >
              <X className="h-[18px] w-[18px] text-[#0A0A0A]" />
            </button>
          </div>
        </div>
      )}
      {dashboardCards.length > 0 && (
        <div className="mb-6 grid gap-4 lg:grid-cols-3">
          {dashboardCards.map((card) => (
            <section
              key={card.id}
              className="rounded-[8px] border bg-white p-4"
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="truncate text-sm font-semibold text-[#0A0A0A]">
                    {card.title}
                  </h2>
                  <p className="mt-1 text-xs capitalize text-[#737373]">
                    {card.source.replace(/_/g, " ")} · {card.visualization}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label={`Remove ${card.title}`}
                  onClick={() => deleteDashboardCard.mutate(card.id)}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] border text-[#737373] hover:bg-[#FAFAFA] hover:text-[#0A0A0A]"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {renderDashboardCardContent(card)}
            </section>
          ))}
        </div>
      )}
      <ChartAreaInteractive
        setTimeRange={setTimeRange}
        loading={shouldShowMainLoaders}
        timeRange={timeRange}
        seriesOrder={competitorsTableOptimized.map(
          (item: { domain: string }) => item.domain,
        )}
        barValuesBySeries={chartBarValuesBySeries}
        windowDays={normalizedDays}
        chartData={data.chartData.map(
          (entry: Record<string, number | string>) => {
            const domainList = [
              canonicalBrandDomain,
              ...competitorsData.map((item: { domain: string }) =>
                canonicalizeDomain(item.domain),
              ),
            ].filter((domain): domain is string => Boolean(domain));

            const aggregatedByDomain = new Map<string, number>();
            Object.entries(entry).forEach(([key, value]) => {
              if (key === "date") return;
              const normalizedKey = String(key);
              const loweredKey = normalizedKey.toLowerCase();
              const canonical = canonicalizeDomain(normalizedKey) || loweredKey;
              const normalizedAlias = normalizeAliasKey(loweredKey);
              const compactAlias = compactAliasKey(loweredKey);
              const resolvedDomain =
                brandDomainAliases.get(canonical) ||
                brandDomainAliases.get(loweredKey) ||
                brandDomainAliases.get(compactAlias) ||
                (normalizedAlias
                  ? brandDomainAliases.get(normalizedAlias) ||
                    brandDomainAliases.get(
                      normalizedAlias.replace(/\s+/g, ""),
                    ) ||
                    brandDomainAliases.get(normalizedAlias.split(" ")[0])
                  : null) ||
                canonical;
              if (!domainList.includes(resolvedDomain)) return;
              const numeric = Number(value);
              if (!Number.isFinite(numeric)) return;
              const existing = aggregatedByDomain.get(resolvedDomain);
              const next =
                existing === undefined ? numeric : Math.max(existing, numeric);
              aggregatedByDomain.set(resolvedDomain, next);
            });

            const newEntry: Record<string, number | string> = {
              date: entry.date as string,
            };
            domainList.forEach((domain: string) => {
              const value = aggregatedByDomain.get(domain) ?? 0;
              newEntry[domain] = Math.min(100, Math.round(value));
            });
            return newEntry;
          },
        )}
        currentBrand={canonicalBrandDomain || ""}
      ></ChartAreaInteractive>
      <div className="flex flex-wrap gap-6 mt-6">
        <SimpleTable
          currentBrand={currentBrand}
          isRunResultLoading={isCompetitorsTableLoading}
          isOnlyYou={showOnlyYou || false}
          title={<LocalizedText id="legacy.competitors.94sz4m" />}
          mainColumnName={t("common.brand")}
          middleColumn={t("common.position")}
          action={handleShowAllCompetitors}
          description={
            <LocalizedText id="legacy.compare.your.brands.visibility.across.chosen.competitors.sv9nis" />
          }
          data={competitorsTableOptimized}
          loadingStateCopy={
            shouldShowCompetitorsLoadingState
              ? {
                  title: t("dashboard.competitors.loadingTitle"),
                  subtitle: t("dashboard.competitors.loadingSubtitle"),
                }
              : null
          }
          competitorBanner={
            shouldShowCompetitorsBanner
              ? {
                  title: t("dashboard.competitors.bannerTitle"),
                  body: t("dashboard.competitors.bannerBody"),
                  buttonLabel: t("dashboard.competitors.bannerButton"),
                  onClick: () => {
                    void handleAddCompetitorCtaClick("banner");
                  },
                }
              : null
          }
          onAddCompetitorClick={() => {
            void handleAddCompetitorCtaClick("card_button");
          }}
        ></SimpleTable>
        <SimpleTable
          isRunResultLoading={shouldShowMainLoaders}
          title={<LocalizedText id="legacy.cited.domains.1iczww9" />}
          action={handleShowAll}
          mainColumnName={t("common.domain")}
          lastColumn={t("common.used")}
          description={
            <LocalizedText id="legacy.domains.ai.platforms.cite.to.answer.your.prompts.vrg10o" />
          }
          data={Object.keys(data.citedDomainsVisibility || {})
            .map((domain) => ({
              domain,
              points: `${data.citedDomainsVisibility[domain].toFixed(0)}%`,
              visability: data.citedDomainsVisibility[domain].toFixed(0),
            }))
            .sort((a, b) => b.visability - a.visability)
            .slice(0, 10)}
        ></SimpleTable>
      </div>
    </div>
  );
}
